import type { ApiSchema } from "../../api/apiSchema";
import { enumMembers } from "../../api/apiSchema";
import { readChangeCounts, type ChangeCounts } from "../../api/content/importProposalsApi";
import { requestJson, withQuery } from "../../api/http/httpClient";
import { readArray, readInteger, readNullableInteger, readObject, readOneOf, readString } from "../../api/http/jsonDecoding";

export type SegmentChangeKind = ApiSchema<"SegmentChangeKind">;
const CHANGES = enumMembers<SegmentChangeKind>({ added: true, modified: true, removed: true, unchanged: true });

export interface TextSnapshot {
  readonly reference: string;
  readonly start: number;
  readonly end: number;
  readonly sha256: string;
}

export interface SegmentChange {
  readonly path: string;
  readonly change: SegmentChangeKind;
  readonly old: TextSnapshot | null;
  readonly new: TextSnapshot | null;
}

export interface SegmentPage {
  readonly head: string;
  readonly documentText: SegmentChange;
  readonly counts: ChangeCounts;
  readonly segments: readonly SegmentChange[];
  readonly offset: number;
  readonly limit: number;
  readonly nextOffset: number | null;
}

export interface TextDiff {
  readonly prefix: number;
  readonly suffix: number;
  readonly removedCharacters: number;
  readonly addedCharacters: number;
  readonly removed: string;
  readonly added: string;
  readonly offset: number;
  readonly limit: number;
  readonly nextOffset: number | null;
}

function readSnapshot(value: unknown): TextSnapshot | null {
  if (value === null) return null;
  const body = readObject(value, "text snapshot");
  return { reference: readString(body, "reference"), start: readInteger(body, "start"), end: readInteger(body, "end"), sha256: readString(body, "text_sha256") };
}

function readSegment(value: unknown): SegmentChange {
  const body = readObject(value, "segment change");
  return { path: readString(body, "path"), change: readOneOf(body, "change", CHANGES), old: readSnapshot(body["old"]), new: readSnapshot(body["new"]) };
}

export function getSegmentPage(id: string, itemId: string, head: string, offset: number, signal: AbortSignal): Promise<SegmentPage> {
  return requestJson({ method: "GET", path: withQuery(`/content/corpus-change-sets/${encodeURIComponent(id)}/documents/${encodeURIComponent(itemId)}/segments`, { reviewed_head: head, offset, limit: 50 }), signal, decodeResponse: (value) => {
    const body = readObject(value, "segment page");
    return { head: readString(body, "head"), documentText: readSegment(body["document_text"]), counts: readChangeCounts(body["counts"]), segments: readArray(body["segments"], "segments", readSegment), offset: readInteger(body, "offset"), limit: readInteger(body, "limit"), nextOffset: readNullableInteger(body, "next_offset") };
  } });
}

export function getTextDiff(id: string, itemId: string, head: string, path: string, offset: number, signal: AbortSignal): Promise<TextDiff> {
  return requestJson({ method: "GET", path: withQuery(`/content/corpus-change-sets/${encodeURIComponent(id)}/documents/${encodeURIComponent(itemId)}/text-diff`, { reviewed_head: head, path, offset, limit: 500 }), signal, decodeResponse: (value) => {
    const body = readObject(value, "text diff");
    return { prefix: readInteger(body, "prefix_characters"), suffix: readInteger(body, "suffix_characters"), removedCharacters: readInteger(body, "removed_characters"), addedCharacters: readInteger(body, "added_characters"), removed: readString(body, "removed"), added: readString(body, "added"), offset: readInteger(body, "offset"), limit: readInteger(body, "limit"), nextOffset: readNullableInteger(body, "next_offset") };
  } });
}
